import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Outlet, NavLink, useParams, useNavigate } from 'react-router-dom';
import {
  CreditCardIcon,
  DocumentTextIcon,
  UserGroupIcon,
  ChartPieIcon,
  ArrowLeftIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const WalletLayout = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  // ---------- Tabs ----------
  const tabs = useMemo(() => {
    if (!eventId) return [];
    return [
      {
        name: 'Recharge',
        path: `/events/${eventId}/wallet/recharge`,
        icon: CreditCardIcon,
      },
      {
        name: 'Transactions',
        path: `/events/${eventId}/wallet/transactions`,
        icon: DocumentTextIcon,
      },
      {
        name: 'Vendors',
        path: `/events/${eventId}/wallet/vendors`,
        icon: UserGroupIcon,
      },
      {
        name: 'Reports',
        path: `/events/${eventId}/wallet/reports`,
        icon: ChartPieIcon,
      },
    ];
  }, [eventId]);

  // ---------- Fetch event ----------
   const fetchEvent = useCallback(
    async (signal) => {
      if (!eventId) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const res = await api.get(`/events/${eventId}`, { signal });
        setEvent(res.data);
        setLoading(false);        // ✅ only on success
      } catch (error) {
        // Ignore abort — component unmounted, don't touch state
        if (
          error.name === 'AbortError' ||
          error.name === 'CanceledError' ||
          error.code === 'ERR_CANCELED'
        ) {
          return;
        }

        console.error('Failed to fetch event:', error);
        setLoading(false);        // ✅ only on a real error
      }
    },
    [eventId],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchEvent(controller.signal);
    return () => controller.abort();
  }, [fetchEvent]);

  // ---------- Loading ----------
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 font-['Barlow']">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-[#708090] text-sm">Loading event wallet...</p>
        </div>
      </div>
    );
  }

  // ---------- Error ----------
  if (!event) {
    return (
      <div className="flex items-center justify-center h-64 font-['Barlow']">
        <div className="text-center">
          <p className="text-red-500 text-lg">Event not found</p>
          <button
            onClick={() => navigate('/events-manager')}
            className="mt-4 text-[#1672b9] hover:underline"
          >
            Go back to Events
          </button>
        </div>
      </div>
    );
  }

  // ---------- Ready ----------
  return (
    <div className="space-y-6 font-['Barlow']">
      {/* ============ HEADER ============ */}
      <div className="flex items-start justify-between gap-4">
        {/* Left: back arrow + title + subtitle */}
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate('/events-manager')}
            className="mt-1 p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Back"
          >
            <ArrowLeftIcon className="w-5 h-5 text-[#2b3445]" />
          </button>
          <div>
            <h1 className="text-xl font-normal leading-7 text-[#2b3445]">
              {event.name} – Wallet
            </h1>
            <p className="text-xs text-[#708090] mt-0.5">
              Manage wallets for attendees of this event
            </p>
          </div>
        </div>

        {/* Right: user avatar + name */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
            {user?.avatar ? (
              <img
                src={user.avatar}
                alt={user.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <UserCircleIcon className="w-6 h-6 text-[#708090]" />
            )}
          </div>
          <span className="text-sm text-[#2b3445] font-medium">
            {user?.name || 'User'}
          </span>
        </div>
      </div>

      {/* ============ TABS BAR ============ */}
           {/* ============ TABS BAR ============ */}
      <div className="inline-flex bg-white border border-[#dae1e7] rounded-lg p-1 shadow-sm">
        <nav className="grid grid-cols-2 md:grid-cols-4 gap-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <NavLink
                key={tab.path}
                to={tab.path}
                className={({ isActive }) =>
                  `flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#e8f1fb] text-[#1471b9]'
                      : 'text-[#2b3445] hover:bg-[#f6f9fc]'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      className={`w-5 h-5 ${
                        isActive ? 'text-[#1471b9]' : 'text-[#708090]'
                      }`}
                    />
                    <span>{tab.name}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* ============ PAGE CONTENT ============ */}
      <Outlet context={{ eventId, event }} />
    </div>
  );
};

export default WalletLayout;